import { useEffect, useRef, useState } from 'react';
import { readAsDataUrl } from '../../../shared/api';
import { Card, Confirm, ErrorBox, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import type { TenantTabProps } from '../TenantDetail';

const MAX_BYTES = 300 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function LogoCard({ slug, detail, reload }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const hasLogo = detail.tenant.branding.hasLogo;
  const [url, setUrl] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // El logo se pide con la sesión puesta (no vale un <img src>) y se enseña como URL de objeto.
  useEffect(() => {
    if (!hasLogo) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoadError(null);
    api
      .blob(`${tenantPath(slug)}/logo`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setUrl(null);
        if ((err as { status?: number }).status !== 404) setLoadError(err);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [slug, hasLogo, version]);

  const upload = async (file: File): Promise<void> => {
    setError(null);
    if (!TYPES.includes(file.type)) {
      setError(new Error('El logo tiene que ser una imagen PNG, JPEG o WebP'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(new Error(`El logo pesa ${Math.round(file.size / 1024)} KB; el máximo son 300 KB`));
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await readAsDataUrl(file);
      await api.put(`${tenantPath(slug)}/logo`, { dataUrl });
      toast('Logo subido', 'ok');
      setVersion((v) => v + 1);
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card title="Logo">
      <div className="stack">
        <div className="row logo-row">
          <div className="logo-box">{url ? <img src={url} alt={`Logo de ${detail.tenant.name}`} /> : <span className="subtle">Sin logo</span>}</div>
          <div className="stack sm grow">
            <p className="muted">Sale en la cabecera del portal, en la pantalla de entrada y en los correos.</p>
            <p className="subtle">PNG, JPEG o WebP de hasta 300 KB. Mejor horizontal y con fondo transparente.</p>
          </div>
        </div>
        <ErrorBox error={loadError ?? error} />
        <div className="row">
          <input
            ref={fileRef}
            type="file"
            accept={TYPES.join(',')}
            className="sr-only"
            id={`logo-${slug}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? 'Subiendo…' : hasLogo ? 'Cambiar logo' : 'Subir logo'}
          </button>
          {hasLogo ? (
            <button className="btn danger" onClick={() => setRemoving(true)} disabled={busy}>
              Quitar logo
            </button>
          ) : null}
        </div>
      </div>

      {removing ? (
        <Confirm
          title="Quitar el logo"
          confirmLabel="Quitar logo"
          danger
          message="El portal enseñará el nombre del cliente en lugar del logo."
          onClose={() => setRemoving(false)}
          onConfirm={async () => {
            await api.put(`${tenantPath(slug)}/logo`, { dataUrl: null });
            toast('Logo quitado', 'ok');
            await reload();
          }}
        />
      ) : null}
    </Card>
  );
}
